import React from 'react';
import {
  ModalFloatingCloseBtn,
  useBreakpointHelpers,
} from '@sweetgreen/garnish';

import { useCustomer } from '@order/Customer';

import {
  CreditCardFormContent,
  useCreditCardHandlers,
} from '../../components/CreditCard';
import { PageWrapper } from '../../components/PageWrapper';
import { TransparentModal } from '../../components/TransparentModal';
import type { BillingAccount } from '../../graphql/types';
import type { PaymentMethodEditScreenProps } from '../../navigation/AppNavigation.props';

export const PaymentMethodEditScreen = ({
  navigation,
  route,
}: PaymentMethodEditScreenProps) => {
  const { paymentMethodId } = route.params;

  const navigateBack = React.useCallback(() => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      navigation.getParent()?.goBack();
    }
  }, [navigation]);

  const { isMobile } = useBreakpointHelpers();

  const {
    submitBillingAccountResponse: { fetching: isSubmitting },
    updateBillingAccountResponse: { fetching: isUpdating },
    deleteBillingAccountResponse: { fetching: isDeleting },
    handleOnEdit,
    handleOnSave,
    handleOnDelete,
  } = useCreditCardHandlers({
    paymentMethodId,
    onComplete: navigateBack,
  });

  const {
    customer: { billingAccounts },
  } = useCustomer();

  const billingAccount = billingAccounts?.find(
    (item: BillingAccount) => item.id === paymentMethodId,
  );

  React.useLayoutEffect(() => {
    if (!billingAccount) {
      navigateBack();
    }
  }, [navigateBack, billingAccount]);

  if (!billingAccount) return null;

  return (
    <TransparentModal size="small" onClose={navigateBack}>
      {isMobile ? null : <ModalFloatingCloseBtn onPress={navigateBack} />}
      <PageWrapper
        withHeaderTitle
        withoutFooter
        testID="account.edit-credit-card.root.container"
      >
        <CreditCardFormContent
          vault="order"
          account={billingAccount}
          isVisible={true}
          isEditing={true}
          isSaving={isSubmitting || isUpdating}
          isDeleting={isDeleting}
          onEdit={handleOnEdit}
          onSave={handleOnSave}
          onDelete={handleOnDelete}
        />
      </PageWrapper>
    </TransparentModal>
  );
};
